import React, {useState} from 'react';
import PropTypes from 'prop-types';
import MakesSelect from "./Vehicle/MakesSelect";
import RangesSelect from "./Vehicle/RangesSelect";
import TypesSelect from "./Vehicle/TypesSelect";
import useAfterUpdateEffect from "../../utils/useAfterUpdateEffect";
import Button from "../Button";
import {ButtonGroup, Card, CardBody, CardHeader, Col, Input, Row} from "reactstrap";
import apiUser from "../../apiUser";
import useDidMountEffect from "../../utils/useDidMountEffect";
import {isEmpty} from "lodash";
import {toast} from "react-toastify";
import getWorkshopCurrentPlanSettings from "../../utils/getWorkshopCurrentPlanSettings";
import {useTranslation} from "react-i18next";
import tecRmiTypeDetailsParse from "../../utils/tecRmi/tecRmiTypeDetailsParse";
import {TCD_VEHICLE_TYPE_CAR, TCD_VEHICLE_TYPE_TRUCK} from "./TecRmiVehicleTypeSuppliers";
import TecRmiModuleDemoAlert from "./TecRmiModuleDemoAlert";

const SHOW_TYPE_LOCALSTORAGE_KEY = 'tecRmiTypeDefault'

const TypeIdSearch = props => {
  const { t } = useTranslation('tec_rmi');
  const [loadingVin, setLoadingVin] = useState(false)
  const [vin, setVin] = useState('')
  const [vinDecodedData, setVinDecodedData] = useState([])
  const [showCar, setShowCar] = useState(localStorage.getItem(SHOW_TYPE_LOCALSTORAGE_KEY) === TCD_VEHICLE_TYPE_CAR || !localStorage.getItem(SHOW_TYPE_LOCALSTORAGE_KEY))
  const [showTruck, setShowTruck] = useState(localStorage.getItem(SHOW_TYPE_LOCALSTORAGE_KEY) === TCD_VEHICLE_TYPE_TRUCK)
  const showBike = false
  const [makeId, setMakeId] = useState()
  const [rangeId, setRangeId] = useState()
  const [typeId, setTypeId] = useState()

  useDidMountEffect(() => {
    if(props.vin){
      setVin(props.vin)
    }
  })

  useAfterUpdateEffect(() => {
    if(typeId){
      props.onTypeIdChange(typeId, showCar ? TCD_VEHICLE_TYPE_CAR : TCD_VEHICLE_TYPE_TRUCK)

      if(props.onTypeDetailsFetch){
        apiUser.tecRmi.vehicleTree.typeDetails({
          showCar,
          showTruck,
          typeId,
        })
          .then(res => {
            props.onTypeDetailsFetch(tecRmiTypeDetailsParse(res.data[0]))
          })
      }
    }
  }, [typeId])

  useAfterUpdateEffect(() => {
    if(vin.length === 17){
      searchByVin()
    }
  }, [vin])

  useAfterUpdateEffect(() => {
    if(vinDecodedData.length === 1){
      const make = vinDecodedData[0]
      setMakeId(make.MakeId)

      if(make.ranges.length === 1){
        const range = make.ranges[0]
        setRangeId(range.RangeId)
        if(range.types.length === 1){
          const type = range.types[0]
          setTypeId(type.TypeId)
        }
      }
    }
  }, [vinDecodedData])

  const changeShowType = (showCar, showTruck) => {
    setShowCar(showCar)
    setShowTruck(showTruck)

    localStorage.setItem(SHOW_TYPE_LOCALSTORAGE_KEY, showCar ? TCD_VEHICLE_TYPE_CAR : TCD_VEHICLE_TYPE_TRUCK)
  }

  const searchByVin = () => {
    if(loadingVin){
      return
    }
    setLoadingVin(true)
    setMakeId(null)
    setRangeId(null)
    setTypeId(null)
    apiUser.tecRmi.findByVin({
      vin,
      showCar,
      showTruck,
    })
      .then(res => {
        setVinDecodedData(res.data)
        if(isEmpty(res.data)){
          if((showCar && getWorkshopCurrentPlanSettings('tecRmiTypeCar.isActive')) || (showTruck && getWorkshopCurrentPlanSettings('tecRmiTypeTruck.isActive'))){
            toast.info(t('type_id_search.vin_decode_fail'))
          }else{
            toast.warn(t('type_id_search.vin_decoder_available_in_full'))
          }
        }
      })
      .finally(() => setLoadingVin(false))
  }

  const getVinDecodedData = (makeId, rangeId) => {
    if(!makeId){
      return null
    }

    const make = vinDecodedData.find(make => make.MakeId === makeId)

    if(!make){
      return null
    }

    if(rangeId){
      return make.ranges.find(range => range.RangeId === rangeId)
    }

    return make
  }

  return <React.Fragment>

    <div className="d-flex justify-content-center">
      <ButtonGroup className="mx-auto mb-2">
        <Button
          color={showCar ? 'primary' : 'secondary'}
          onClick={() => changeShowType(true, false)}
        >
          <div className="d-flex flex-column justify-content-center p-1">
            <i className="fas fa-car fa-2x mb-2"/>
            <div>{t('type_id_search.show_type_car')}</div>
          </div>
        </Button>
        <Button
          color={showTruck ? 'primary' : 'secondary'}
          onClick={() => changeShowType(false, true)}
        >
          <div className="d-flex flex-column justify-content-center p-1">
            <i className="fas fa-truck fa-2x mb-2"/>
            <div>{t('type_id_search.show_type_truck')}</div>
          </div>
        </Button>
      </ButtonGroup>
    </div>

    <TecRmiModuleDemoAlert
      showType={showCar ? TCD_VEHICLE_TYPE_CAR : TCD_VEHICLE_TYPE_TRUCK}
      tecRmiType={showCar ? getWorkshopCurrentPlanSettings('tecRmiTypeCar') : getWorkshopCurrentPlanSettings('tecRmiTypeTruck')}
    />

    <Row>
      <Col xs={12} sm={6}>
        <Card>
          <CardHeader>{t('type_id_search.decode_vin_header')}</CardHeader>
          <CardBody>
            <div className="mx-auto w-100" style={{ maxWidth: 350 }}>
              <Input
                className="mx-auto mb-2"
                placeholder={t('type_id_search.vin_input_placeholder')}
                onChange={e => setVin(e.target.value)}
                value={vin}
              />
              <Button
                className="mx-auto w-100"
                loading={loadingVin}
                disabled={!vin}
                onClick={searchByVin}
              >{t('type_id_search.btn_decode')}</Button>
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col xs={12} sm={6}>
        <Card>
          <CardHeader>{t('type_id_search.search_header')}</CardHeader>
          <CardBody>
            <div className="mb-2">
              <MakesSelect
                makeId={makeId}
                makeName={props.makeName}
                onChange={setMakeId}
                showCar={showCar}
                showTruck={showTruck}
                showBike={showBike}
              />
            </div>
            <RangesSelect
              rangeId={rangeId}
              rangesIdFilter={getVinDecodedData(makeId)?.ranges?.map(range => range.RangeId) || []}
              rangeName={props.rangeName}
              makeId={makeId}
              showCar={showCar}
              showTruck={showTruck}
              showBike={showBike}
              onChange={setRangeId}
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
    <Row>
      <Col>
        {rangeId && <Card>
          <CardHeader>{t('type_id_search.select_type_header')}</CardHeader>
          <CardBody>
            <TypesSelect
              rangeId={rangeId}
              typesIdFilter={getVinDecodedData(makeId, rangeId)?.types?.map(type => type.TypeId) || []}
              showCar={showCar}
              showTruck={showTruck}
              showBike={showBike}
              onChange={setTypeId}
            />
          </CardBody>
        </Card>}
      </Col>
    </Row>
  </React.Fragment>
};

TypeIdSearch.propTypes = {
  vin: PropTypes.string,
  makeName: PropTypes.string,
  rangeName: PropTypes.string,
  onTypeIdChange: PropTypes.func.isRequired,
  onTypeDetailsFetch: PropTypes.func,
};

export default TypeIdSearch;